import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { DatePicker } from '@client/components/_ui/data-entry/DatePicker';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { MonthDateGrid } from '@client/components/workload-manager/MonthDateGrid';
import { WorkloadManagerFilters } from '@client/components/workload-manager/WorkloadManagerFilters';
import {
    OPEN_WORKLOAD_ITEMS_WHERE,
    WorkloadManagerTable,
} from '@client/components/workload-manager/WorkloadManagerTable';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { useAntdTable } from '@client/hooks/table/useAntdTable';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { getTaskStatus } from '@client/utils';
import { startOfCalendarWeek } from '@client/utils/common/dateTime';
import { TASK_STATUS_CONFIG } from '@client/utils/status/status-config';
import { formatDate } from '@shared/utils/core/formatting';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
// curium:allow-wrapped-import — the raw Table and Tooltip predate the ratchet; this line only lost Spin (CUR-1341)
import { Card, Col, Row, Table, Tooltip, Typography } from 'antd';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { z } from 'zod';

const VIEW_OPTIONS = ['day', 'week', 'month'] as const;

type ViewOption = (typeof VIEW_OPTIONS)[number];

export const Route = createFileRoute('/console/$orgId/workload-manager/tasks/calendar')({
    staticData: {
        metadata: {
            title: 'Task calendar',
        },
        breadcrumb: {
            title: 'Tasks',
        },
    },
    validateSearch: zodValidator(
        z.object({
            view: z.enum(VIEW_OPTIONS).default('month'),
            viewDate: z.coerce.date().optional(),
            date: z.coerce.date().default(new Date()),
        }),
    ),
    component: TaskCalendar,
});

function LeftCalendar() {
    const { date } = Route.useSearch();
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);

    // Workaround for warning in browser console when we set open={true} directly
    useEffect(() => {
        setOpen(true);
    }, []);

    const CLASS_NAME = 'left-calendar-container';

    return (
        <Card classNames={{ body: clsx(CLASS_NAME, 'pt-4 pb-3') }}>
            <DatePicker
                open={open}
                value={date}
                getPopupContainer={() => document.querySelector(`.${CLASS_NAME}`)!}
                className="hidden"
                classNames={{
                    popup: {
                        root: clsx(
                            'static',
                            '[&_.ant-picker-panel-container]:shadow-none',
                            '[&_.ant-picker-panel-container]:bg-transparent',
                            '[&_.ant-picker-month-panel]:w-auto',
                            '[&_.ant-picker-year-panel]:w-auto',
                            '[&_.ant-picker-decade-panel]:w-auto',
                            '[&_.ant-picker-header]:px-0',
                            '[&_.ant-picker-body]:px-0 [&_.ant-picker-body]:pb-0',
                            '[&_.ant-picker-date-panel]:w-full',
                        ),
                    },
                }}
                onChange={(date) =>
                    navigate({
                        from: Route.fullPath,
                        search(prev) {
                            return {
                                ...prev,
                                date,
                                viewDate: undefined,
                            };
                        },
                    })
                }
            />
        </Card>
    );
}

function DayView() {
    const searchParams = Route.useSearch();
    const [workloadFilters] = usePathState<RouterInputs['workloadManager']['listWorkloadItems']['where']>(
        'workload-filters-where',
        {},
    );

    const now = DateTime.now();
    const date = DateTime.fromJSDate(searchParams.viewDate || searchParams.date);
    const endOfDateDay = date.endOf('day');

    return (
        <WorkloadManagerTable
            skipFetching={endOfDateDay.toMillis() < now.startOf('day').toMillis()}
            filters={{
                dueDate: {
                    ...(!date.hasSame(now, 'day') && { gte: date.startOf('day').toJSDate() }),
                    lte: endOfDateDay.toJSDate(),
                },
                ...workloadFilters,
            }}
        />
    );
}

function TaskTag({
    id,
    title,
    dueDate,
    isCompleted,
    workItemType,
    className,
    tabIndex,
}: {
    id: bigint;
    title: string;
    dueDate: Date;
    isCompleted: boolean;
    workItemType: string;
    className?: string;
    tabIndex?: number;
}) {
    const orgId = useOrgId();

    // Create appropriate routes for each workload item type
    const getRoute = () => {
        switch (workItemType) {
            case 'task':
                return {
                    to: '/console/$orgId/workload-manager/tasks/calendar/details/$id' as const,
                    params: { orgId, id },
                };
            case 'control_activity':
                return {
                    to: '/console/$orgId/workload-manager/tasks/calendar/details/control_activity/$id' as const,
                    params: { orgId, id },
                };
            case 'control_test':
                return {
                    to: '/console/$orgId/workload-manager/tasks/calendar/details/control_test/$id' as const,
                    params: { orgId, id },
                };
            case 'risk_action':
                return {
                    to: '/console/$orgId/workload-manager/tasks/calendar/details/risk_action/$id' as const,
                    params: { orgId, id },
                };
            case 'msp_governance_check':
                return {
                    to: '/console/$orgId/workload-manager/tasks/calendar/details/msp_governance_check/$id' as const,
                    params: { orgId, id },
                };
            default:
                // Fallback to task route
                return {
                    to: '/console/$orgId/workload-manager/tasks/calendar/details/$id' as const,
                    params: { orgId, id },
                };
        }
    };

    const route = getRoute();
    const statusConfig = TASK_STATUS_CONFIG[getTaskStatus({ dueDate, isCompleted })];

    return (
        <Tooltip title={title}>
            <Button.Link
                to={route.to}
                tabIndex={tabIndex}
                params={route.params}
                variant="link"
                className={clsx('mb-2 flex w-full no-underline', className)}
                onClick={(e) => e.stopPropagation()}
            >
                <span
                    className={clsx(
                        'block w-full truncate rounded border px-2 py-0.5 text-left text-xs',
                        statusConfig.bgClass,
                        statusConfig.textClass,
                        statusConfig.borderClass,
                    )}
                >
                    {title || 'Unnamed task'}
                </span>
            </Button.Link>
        </Tooltip>
    );
}

function WeekView() {
    const searchParams = Route.useSearch();
    const [workloadFilters] = usePathState<RouterInputs['workloadManager']['listWorkloadItems']['where']>(
        'workload-filters-where',
        {},
    );
    const navigate = useNavigate();

    const now = DateTime.now();
    const date = DateTime.fromJSDate(searchParams.viewDate || searchParams.date);
    const startOfWeek = startOfCalendarWeek(date);
    const startOfCurrentWeek = startOfCalendarWeek(now);
    const endOfWeek = startOfWeek.plus({ days: 6 }).endOf('day');

    const { data, isLoading } = trpc.workloadManager.listWorkloadItems.useQuery(
        endOfWeek.toMillis() < startOfCurrentWeek.toMillis()
            ? skipToken
            : {
                  where: {
                      AND: [
                          {
                              dueDate: {
                                  ...(startOfWeek.toMillis() !== startOfCurrentWeek.toMillis() && {
                                      gte: startOfWeek.toJSDate(),
                                  }),
                                  lte: endOfWeek.toJSDate(),
                              },
                              ...workloadFilters,
                          },
                          OPEN_WORKLOAD_ITEMS_WHERE,
                      ],
                  },
                  orderBy: [{ dueDate: 'asc' }, { title: 'asc' }],
                  limit: 1000,
                  offset: 0,
              },
        {
            select(data) {
                const now = new Date();

                return {
                    rows: [
                        data.rows.reduce(
                            (prev, item) => {
                                const key = formatDate(getTaskStatus(item) === 'OVERDUE' ? now : item.dueDate);

                                return {
                                    ...prev,
                                    [key]: [...(prev[key] || []), item],
                                };
                            },
                            {} as Record<string, typeof data.rows>,
                        ),
                    ],
                    total: 1,
                };
            },
        },
    );

    type Row = NonNullable<typeof data>['rows'][number];

    const { tableProps } = useAntdTable<Row>({
        rowKey() {
            return 'key';
        },
        data: {
            rows: data?.rows,
            loading: isLoading,
            total: data?.total,
        },
        columns: Array.from({ length: 7 }, (_, i) => {
            const updatedDate = startOfWeek.plus({ days: i }).toJSDate();
            const dateShortFormat = formatDate(updatedDate);
            const isSelectedDate = dateShortFormat === formatDate(searchParams.date);

            return {
                title: (
                    <>
                        {formatDate(updatedDate, 'EEEE')}
                        <br />
                        {updatedDate.getDate()}
                    </>
                ),
                dataIndex: dateShortFormat,
                align: 'center',
                onCell() {
                    return {
                        className: clsx(
                            'cursor-pointer p-0 align-top',
                            dateShortFormat === formatDate(new Date()) && 'border-t-2 border-t-primary',
                            isSelectedDate && 'bg-accent-50 hover:bg-accent-50!',
                            !isSelectedDate && 'hover:bg-(--ant-control-item-bg-hover)',
                        ),
                        onClick(e) {
                            navigate(
                                {
                                    from: Route.fullPath,
                                    search(prev) {
                                        return {
                                            ...prev,
                                            date: updatedDate,
                                            viewDate: undefined,
                                            view: 'day',
                                        };
                                    },
                                },
                                e,
                            );
                        },
                    };
                },
                render(value: Row[string]) {
                    return (
                        <div className="max-h-100 min-h-20 overflow-y-auto p-(--ant-table-cell-padding-block)">
                            {value?.map((item) => (
                                <TaskTag
                                    key={`${item.workItemType}-${item.id}`}
                                    className="last:mb-0"
                                    id={item.id}
                                    title={item.title || 'Untitled'}
                                    dueDate={item.dueDate || new Date()}
                                    isCompleted={item.isCompleted || false}
                                    workItemType={item.workItemType}
                                />
                            ))}
                        </div>
                    );
                },
            };
        }),
    });

    return (
        <Table
            rowHoverable={false}
            tableLayout="fixed"
            bordered
            onRow={() => ({
                className: 'border-b-0',
            })}
            {...tableProps}
        />
    );
}

function MonthView() {
    const searchParams = Route.useSearch();
    const [workloadFilters] = usePathState<RouterInputs['workloadManager']['listWorkloadItems']['where']>(
        'workload-filters-where',
        {},
    );
    const navigate = useNavigate();

    const now = DateTime.now();
    const date = DateTime.fromJSDate(searchParams.viewDate || searchParams.date);
    const endOfMonth = date.endOf('month');

    const { data, isLoading } = trpc.workloadManager.listWorkloadItems.useQuery(
        endOfMonth.toMillis() < now.startOf('month').toMillis()
            ? skipToken
            : {
                  where: {
                      AND: [
                          {
                              dueDate: {
                                  ...(!date.hasSame(now, 'month') && { gte: date.startOf('month').toJSDate() }),
                                  lte: endOfMonth.toJSDate(),
                              },
                              ...workloadFilters,
                          },
                          OPEN_WORKLOAD_ITEMS_WHERE,
                      ],
                  },
                  orderBy: [{ dueDate: 'asc' }, { title: 'asc' }],
                  limit: 1000,
                  offset: 0,
              },
        {
            select(data) {
                const now = new Date();

                return {
                    ...data,
                    rows: data.rows.reduce(
                        (prev, item) => {
                            const key = formatDate(getTaskStatus(item) === 'OVERDUE' ? now : item.dueDate);

                            return {
                                ...prev,
                                [key]: [...(prev[key] || []), item],
                            };
                        },
                        {} as Record<string, typeof data.rows>,
                    ),
                };
            },
        },
    );
    return (
        <Loading spinning={isLoading} className="max-h-none" keepWrapper>
            <MonthDateGrid
                selectedDate={searchParams.date}
                viewedDate={date.toJSDate()}
                onViewDateChange={(viewDate) =>
                    navigate({
                        from: Route.fullPath,
                        replace: true,
                        search: (prev) => ({ ...prev, viewDate }),
                    })
                }
                onSelect={(date) =>
                    navigate({
                        from: Route.fullPath,
                        search(prev) {
                            return {
                                ...prev,
                                date,
                                viewDate: undefined,
                                view: 'day',
                            };
                        },
                    })
                }
                describeDate={(date) => {
                    if (!data) return undefined;
                    const count = data.rows[formatDate(date)]?.length ?? 0;
                    return `${count} ${count === 1 ? 'task' : 'tasks'}`;
                }}
                renderDate={(date, taskTabIndex) =>
                    data?.rows[formatDate(date)]?.map((item) => (
                        <TaskTag
                            tabIndex={taskTabIndex}
                            key={`${item.workItemType}-${item.id}`}
                            id={item.id}
                            title={item.title || 'Untitled'}
                            dueDate={item.dueDate || new Date()}
                            isCompleted={item.isCompleted || false}
                            workItemType={item.workItemType}
                        />
                    ))
                }
            />
        </Loading>
    );
}

function DateSelectorLabel() {
    const { view, ...searchParams } = Route.useSearch();

    const date = searchParams.viewDate || searchParams.date;

    if (view === 'day') {
        return formatDate(date, 'dd MMM yyyy');
    }

    if (view === 'week') {
        const startOfWeek = startOfCalendarWeek(DateTime.fromJSDate(date));
        const endOfWeek = startOfWeek.plus({ days: 6 }).endOf('day');

        return `${startOfWeek.day} ${
            endOfWeek.day < startOfWeek.day
                ? formatDate(startOfWeek.toJSDate(), startOfWeek.year < endOfWeek.year ? 'MMM yyyy' : 'MMM')
                : ''
        } - ${endOfWeek.day} ${formatDate(endOfWeek.toJSDate(), 'MMM yyyy')}`;
    }

    return formatDate(date, 'MMM yyyy');
}

function TaskCalendar() {
    const { view } = Route.useSearch();
    const navigate = useNavigate();
    const [, setWorkloadFilters] = usePathState<RouterInputs['workloadManager']['listWorkloadItems']['where']>(
        'workload-filters-where',
        {},
    );

    return (
        <PageController route={Route}>
            <Outlet />
            <TableToolbar
                filters={
                    <WorkloadManagerFilters
                        onChange={(where) => {
                            setWorkloadFilters(where);
                        }}
                    />
                }
            />
            <Row gutter={16}>
                <Col span={6}>
                    <LeftCalendar />
                </Col>
                <Col span={18}>
                    <Card>
                        <div className="relative mb-4 flex justify-center">
                            <div className="absolute left-0 flex items-center gap-2 text-subtle-foreground">
                                <Button.Link
                                    icon={<LeftOutlined />}
                                    aria-label={`Previous ${view}`}
                                    variant="link"
                                    from={Route.fullPath}
                                    search={(prev) => ({
                                        ...prev,
                                        viewDate: DateTime.fromJSDate(prev.viewDate || prev.date)
                                            .minus({
                                                [view === 'day' ? 'days' : view === 'week' ? 'weeks' : 'months']: 1,
                                            })
                                            .toJSDate(),
                                    })}
                                />
                                <Typography.Text strong>
                                    <DateSelectorLabel />
                                </Typography.Text>
                                <Button.Link
                                    icon={<RightOutlined />}
                                    aria-label={`Next ${view}`}
                                    variant="link"
                                    from={Route.fullPath}
                                    search={(prev) => ({
                                        ...prev,
                                        viewDate: DateTime.fromJSDate(prev.viewDate || prev.date)
                                            .plus({
                                                [view === 'day' ? 'days' : view === 'week' ? 'weeks' : 'months']: 1,
                                            })
                                            .toJSDate(),
                                    })}
                                />
                            </div>
                            <SegmentedControl<ViewOption>
                                value={view}
                                onChange={(value) =>
                                    navigate({
                                        from: Route.fullPath,
                                        search(prev) {
                                            return {
                                                ...prev,
                                                view: value,
                                            };
                                        },
                                    })
                                }
                                options={[
                                    { label: 'Day', value: 'day' },
                                    { label: 'Week', value: 'week' },
                                    { label: 'Month', value: 'month' },
                                ]}
                                aria-label="Select calendar view mode"
                            />
                        </div>
                        {view === 'day' && <DayView />}
                        {view === 'week' && <WeekView />}
                        {view === 'month' && <MonthView />}
                    </Card>
                </Col>
            </Row>
        </PageController>
    );
}
